"use client";

import { FC, useState, useCallback } from "react";
import { cn } from "utils";
import {
  CollapseProps,
  CollapsePanelProps,
  CollapseClassNames,
  CollapseLabelRender,
} from "./types";

const CollapsePanel = ({
  panel,
  isExpanded,
  onToggle,
  expandIcon,
  classNames,
}: {
  panel: CollapsePanelProps;
  isExpanded: boolean;
  onToggle: () => void;
  expandIcon?: (
    panelProps: CollapsePanelProps & { isExpanded: boolean },
  ) => React.ReactNode;
  classNames?: CollapseClassNames;
}) => {
  // classNames 已经是解析后的 CollapseClassNames 类型
  const panelClassNames = classNames;

  return (
    <div className={cn("flex flex-col", panelClassNames?.panel)}>
      <div
        data-openreplay-label="Collapse · Collapse · div"
        className={cn(
          "flex justify-between items-center cursor-pointer group",
          panelClassNames?.header,
        )}
        onClick={onToggle}
      >
        <div
          className={cn(
            "duration-200 transition-all",
            panelClassNames?.headerLabel,
          )}
        >
          {typeof panel.label === "function"
            ? (panel.label as CollapseLabelRender)(panel, isExpanded)
            : panel.label}
        </div>
        {expandIcon && (
          <div className={panelClassNames?.icon}>
            {expandIcon({ ...panel, isExpanded })}
          </div>
        )}
      </div>
      <div
        className={cn(
          "overflow-hidden transition-all duration-300 ease-in-out",
          panelClassNames?.content,
          isExpanded ? "max-h-[500px] opacity-100 mt-3" : "max-h-0 opacity-0",
        )}
      >
        <div className={panelClassNames?.contentInner}>{panel.children}</div>
      </div>
    </div>
  );
};

export const Collapse: FC<CollapseProps> = ({
  data,
  expandIcon,
  classNames,
  className,
  defaultActiveKey,
  accordion = true,
  onChange,
}) => {
  // 将 defaultActiveKey 转换为 Set
  const getDefaultActiveKeys = useCallback(() => {
    if (defaultActiveKey === undefined) {
      return new Set<string>();
    }
    if (Array.isArray(defaultActiveKey)) {
      return new Set(defaultActiveKey);
    }
    return new Set([defaultActiveKey]);
  }, [defaultActiveKey]);

  const [activeKeys, setActiveKeys] =
    useState<Set<string>>(getDefaultActiveKeys);

  const handleToggle = useCallback(
    (key: string) => {
      setActiveKeys((prev) => {
        const newKeys = new Set(prev);

        if (accordion) {
          // 手风琴模式：同时只能展开一个
          if (newKeys.has(key)) {
            newKeys.clear();
          } else {
            newKeys.clear();
            newKeys.add(key);
          }
        } else {
          // 普通模式：可以同时展开多个
          if (newKeys.has(key)) {
            newKeys.delete(key);
          } else {
            newKeys.add(key);
          }
        }

        onChange?.(Array.from(newKeys));
        return newKeys;
      });
    },
    [accordion, onChange],
  );

  return (
    <div
      className={cn(
        "w-full flex flex-col divide-y divide-border_white_transparent10 space-y-6 md:space-y-4",
        className,
        typeof classNames === "object" ? classNames?.root : undefined,
      )}
    >
      {data.map((panel, index) => {
        const isExpanded = activeKeys.has(panel.key);
        // 如果 classNames 是函数，则调用它获取当前 panel 的样式
        const panelClassNames =
          typeof classNames === "function" ? classNames(panel) : classNames;

        return (
          <div
            key={panel.key}
            className={cn(
              { "pt-6 md:pt-4": index !== 0 },
              panelClassNames?.itemWrapper,
            )}
          >
            <CollapsePanel
              panel={panel}
              isExpanded={isExpanded}
              onToggle={() => handleToggle(panel.key)}
              expandIcon={expandIcon}
              classNames={panelClassNames}
            />
          </div>
        );
      })}
    </div>
  );
};
